import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  NavbarItem,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import {
  useGetPatientQuery,
  useUpdatePatientMutation,
  useReadEventMutation,
} from '@/patient-app-common/api/patientApi';
import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import { useAppSelector } from '@/redux/hooks';

export function NavDemo() {
  const { demo } = useAppSelector(({ auth }) => auth);

  const { completedEvents } = useAppEvents();

  const { data: patient } = useGetPatientQuery();
  const [updatePatient, { isLoading: isLoadingUpdatePatient }] =
    useUpdatePatientMutation();
  const [readEvent, { isLoading: isLoadingReadEvent }] = useReadEventMutation();
  const isLoading = isLoadingUpdatePatient || isLoadingReadEvent;

  const PROGRAM_TYPE = useMemo(() => {
    let program = 'surgical';

    if (patient?.maternityProgram) {
      program = 'maternity';
    }

    if (patient?.nonSurgicalProgram) {
      program = 'chronic';
    }

    return program;
  }, [patient]);

  if (!demo || !patient) return null;

  const setSurgicalDate = async (days) => {
    const surgeryDate = dayjs().startOf('day').add(days, 'days').unix();

    const params = {
      confirmedSurgery: false,
      surgeryDate,
      dischargeDate: 0,
      completedDate: 0,
    };

    await updatePatient(params);
  };

  const setDischargeDate = async (days) => {
    const dischargeDate = dayjs().startOf('day').add(days, 'days').unix();
    const surgeryDate = dayjs()
      .startOf('day')
      .add(days - 1, 'days')
      .unix();

    const params = {
      confirmedSurgery: true,
      surgeryDate,
      dischargeDate,
      completedDate: 0,
    };

    await updatePatient(params);
  };

  const setSignupDate = async (days) => {
    const signupTime = dayjs().startOf('day').add(days, 'days').unix();

    const params = {
      createdAt: signupTime,
      completedDate: 0,
    };
    await updatePatient(params);
  };

  const setEndProgram = async () => {
    await updatePatient({ completedDate: dayjs().unix() });
  };

  const resetEvents = async () => {
    const eventsToUnread = [...completedEvents];

    await Promise.all(
      eventsToUnread.map(async (event) => {
        await readEvent({
          eventId: event.applicationEventId,
          value: false.toString(),
        }).unwrap();
      })
    );
  };

  return (
    <Dropdown>
      <NavbarItem>
        <DropdownTrigger>
          <Button
            isLoading={isLoading}
            disableRipple
            className="bg-transparent data-[hover=true]:bg-transparent"
            endContent={<Icon icon="mdi:chevron-down" />}
            variant="light"
          >
            Demo
          </Button>
        </DropdownTrigger>
      </NavbarItem>
      <DropdownMenu color="primary" variant="flat">
        {PROGRAM_TYPE === 'surgical' && (
          <DropdownSection showDivider>
            <DropdownItem onPress={() => setSurgicalDate(7)}>
              7 days before surgery
            </DropdownItem>
            <DropdownItem onPress={() => setSurgicalDate(1)}>
              1 day before surgery
            </DropdownItem>
            <DropdownItem onPress={() => setSurgicalDate(-1)}>
              1 day after surgery
            </DropdownItem>
            <DropdownItem onPress={() => setDischargeDate(0)}>
              Day of discharge
            </DropdownItem>
            <DropdownItem onPress={setEndProgram}>End of program</DropdownItem>
          </DropdownSection>
        )}
        {PROGRAM_TYPE === 'maternity' && (
          <DropdownSection showDivider>
            <DropdownItem onPress={() => setSurgicalDate(27 * 7)}>
              13 weeks pregnant (2nd trimester)
            </DropdownItem>
            <DropdownItem onPress={() => setSurgicalDate(13 * 7)}>
              27 weeks pregnant (3rd trimester)
            </DropdownItem>
            <DropdownItem onPress={() => setSurgicalDate(7)}>
              1 week before due date
            </DropdownItem>
            <DropdownItem onPress={() => setSurgicalDate(1)}>
              1 day before due date
            </DropdownItem>
            <DropdownItem onPress={() => setSurgicalDate(0)}>
              Due date
            </DropdownItem>
            <DropdownItem onPress={() => setDischargeDate(0)}>
              Day of discharge
            </DropdownItem>
            <DropdownItem onPress={() => setDischargeDate(-7)}>
              1 week after discharge
            </DropdownItem>
            <DropdownItem onPress={setEndProgram}>End of program</DropdownItem>
          </DropdownSection>
        )}
        {PROGRAM_TYPE === 'chronic' && (
          <DropdownSection showDivider>
            <DropdownItem onPress={() => setSignupDate(0)}>
              Day of sign up
            </DropdownItem>
            <DropdownItem onPress={() => setSignupDate(-7)}>
              7 days after sign up
            </DropdownItem>
            <DropdownItem onPress={() => setSignupDate(-14)}>
              14 days after sign up
            </DropdownItem>
            <DropdownItem onPress={() => setSignupDate(-28)}>
              28 days after sign up
            </DropdownItem>
            <DropdownItem onPress={setEndProgram}>End of program</DropdownItem>
          </DropdownSection>
        )}
        <DropdownItem href="/tracker/daily">Do daily health check</DropdownItem>
        <DropdownItem onPress={resetEvents}>Reset all messages</DropdownItem>
        <DropdownItem href="/demo">More demo controls</DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}
